import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGetApiServiceDetailQuery } from '../../../../redux/api';
import { K, apiT, apiLabel, getAdapterTypeLabel } from '../apiServiceI18n';
import {
  buildApiServiceBaseUrlPreview,
  mapApiServiceDetailResponse,
  mapApiServiceDetailToForm,
  resolveServiceAccount,
} from '../utils/apiServiceUtils';
import CustomUrlDrawer from '../CustomUrlDrawer';
import ApiServiceCancelWarningDialog from '../ApiServiceCancelWarningDialog';
import { validateApiOrWorkflowName, validateExecuteWithin } from '../validateApiServiceWizard';
import {
  applyApiServiceEdit,
  formatCustomUrlPublicPreview,
  resolveCustomUrlPath,
  validateApiServiceName,
  validateApiCustomUrl,
} from '../apiServiceApiHelpers';
import { openWorkbenchWorkflow } from '../apiServiceWizardSession';

/**
 * Edit API/Service (BR3.4) and View API/Service (BR3.5) — left drawer on Overview.
 */
function ApiServiceEditDrawer({ target, mode = 'edit', onClose }) {
  const isView = mode === 'view';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { account: reduxAccount } = useSelector((s) => s.queryParams);

  const account = resolveServiceAccount(reduxAccount, target?.account);
  const wfgName = target?.workflowGroup ?? '';
  const wfName = target?.workflowName ?? '';

  const detailQuery = useGetApiServiceDetailQuery(
    { account, wfg_name: wfgName, wf_name: wfName },
    { skip: !target || !account || !wfgName || !wfName, refetchOnMountOrArgChange: true }
  );

  const { detail, error: detailError } = useMemo(
    () => mapApiServiceDetailResponse(detailQuery.data),
    [detailQuery.data]
  );

  // ── Form state ────────────────────────────────────────────────────────────
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [customDrawerOpen, setCustomDrawerOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const originalApiNameRef = useRef('');

  useEffect(() => {
    if (!target) {
      setForm(null);
      setErrors({});
      return;
    }
    if (!detail) return;
    setForm(mapApiServiceDetailToForm(detail));
    originalApiNameRef.current = detail.name || '';
  }, [target, detail]);

  // ── Dirty detection ───────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (!form || !detail) return false;
    return JSON.stringify(form) !== JSON.stringify(mapApiServiceDetailToForm(detail));
  }, [form, detail]);

  const baseUrlPreview = useMemo(
    () =>
      buildApiServiceBaseUrlPreview({
        account: detail?.account || account,
        workflowGroup: detail?.workflowGroup || wfgName,
        workflowName: detail?.workflowName || wfName,
        pathBase: detail?.baseUrl,
      }),
    [account, wfgName, wfName, detail]
  );

  const customUrlSummary = useMemo(
    () => formatCustomUrlPublicPreview(form?.customUrl?.from),
    [form?.customUrl?.from]
  );

  // ── Form helpers ──────────────────────────────────────────────────────────
  const onChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const copyToClipboard = (text) => {
    if (text) navigator.clipboard.writeText(text).catch(() => {});
  };

  // ── Apply logic ───────────────────────────────────────────────────────────
  /**
   * Run client + server-side validation. Returns an errors object (empty = valid).
   */
  const runValidation = useCallback(async () => {
    const errs = {};

    // Name: format check
    const nameResult = validateApiOrWorkflowName(form.apiName);
    if (!nameResult.ok) {
      errs.apiName = nameResult.message;
    } else if (form.apiName.trim() !== originalApiNameRef.current.trim()) {
      // Name changed — check uniqueness on server (exclude-triplet is implicit: same account context)
      const nameServerResult = await validateApiServiceName(dispatch, {
        account: detail.account,
        serviceName: form.apiName,
      });
      if (!nameServerResult.ok) errs.apiName = nameServerResult.message;
    }

    // Conformance execute-within value
    if (form.conformance && form.conformanceExecute) {
      const execResult = validateExecuteWithin(form.executeWithin);
      if (!execResult.ok) errs.executeWithin = execResult.message;
    }

    // Custom URL: validate only if changed and adapter is HTTP
    if (detail.adapter === 'BridgeGateHTTP') {
      const newFrom = resolveCustomUrlPath(form.customUrl);
      const oldFrom = (detail.customUrl || '').trim();
      if (newFrom !== oldFrom) {
        const urlResult = await validateApiCustomUrl(dispatch, {
          account: detail.account,
          customUrl: form.customUrl,
          workflowGroup: detail.workflowGroup,
          workflowName: detail.workflowName,
        });
        if (!urlResult.ok) errs.customUrl = urlResult.message;
      }
    }

    return errs;
  }, [form, detail, dispatch]);

  const performApply = useCallback(async () => {
    setApplying(true);
    try {
      const result = await applyApiServiceEdit(dispatch, { detail, form });
      if (result.ok) {
        onClose({ success: true });
      } else {
        setToast({
          open: true,
          message: result.message || apiT(K.API_SVC_APPLY_FAILED),
          severity: 'error',
        });
      }
    } finally {
      setApplying(false);
    }
  }, [dispatch, detail, form, onClose]);

  const handleApplyClick = useCallback(async () => {
    const errs = await runValidation();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    await performApply();
  }, [runValidation, performApply]);

  const requestClose = useCallback(() => {
    if (isView || !isDirty) {
      onClose();
      return;
    }
    setCancelDialogOpen(true);
  }, [isView, isDirty, onClose]);

  const handleCancelClick = requestClose;

  // "Discard changes" option in cancel dialog
  const handleDiscardChanges = () => {
    setCancelDialogOpen(false);
    onClose();
  };

  // "Save progress" option — run full apply, then close
  const handleSaveAndClose = useCallback(async () => {
    setCancelDialogOpen(false);
    const errs = await runValidation();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    await performApply();
  }, [runValidation, performApply]);

  // ── Modify Workflow ───────────────────────────────────────────────────────
  const handleModifyWorkflow = () => {
    if (!detail?.account || !detail.workflowGroup || !detail.workflowName) return;
    openWorkbenchWorkflow({
      account: detail.account,
      workflowGroup: detail.workflowGroup,
      workflowName: detail.workflowName,
    });
  };

  const isLoading = detailQuery.isFetching;
  const loadError =
    detailError ||
    detailQuery.error?.data?.message ||
    detailQuery.error?.error ||
    (!isLoading && target && !detail ? apiT(K.API_SVC_EDIT_LOAD_FAILED) : null);
  const conformanceDisabled = !form?.conformance;
  const drawerTitle = isView ? t(K.API_SVC_VIEW_TITLE) : t(K.API_SVC_EDIT_TITLE);
  const emDash = t(K.API_SVC_EM_DASH);

  const formBody =
    isLoading ? (
      <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    ) : loadError ? (
      <Alert severity="error">{loadError}</Alert>
    ) : form ? (
      <Stack spacing={2.5}>
        {/* Name */}
        <TextField
          required={!isView}
          label={apiLabel(K.LBL_NAME)}
          value={form.apiName}
          onChange={(e) => onChange('apiName', e.target.value)}
          error={Boolean(errors.apiName)}
          helperText={errors.apiName}
          disabled={isView}
          fullWidth
          size="small"
        />

        <TextField
          label={t(K.API_SVC_API_SERVICE_VERSION)}
          value={detail?.version || emDash}
          disabled
          fullWidth
          size="small"
        />

        {/* Description */}
        <TextField
          label={t(K.API_SVC_DESCRIPTION)}
          value={form.description}
          onChange={(e) => onChange('description', e.target.value)}
          disabled={isView}
          fullWidth
          multiline
          minRows={3}
          size="small"
        />

        {/* API/Service Type — read-only in both modes */}
        <TextField
          label={t(K.API_SVC_API_SERVICE_TYPE)}
          value={getAdapterTypeLabel(form.apiType)}
          disabled
          fullWidth
          size="small"
        />

        {/* Workflow Group / Workflow Name — read-only in both modes */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label={apiLabel(K.WORKFLOW_GROUP)}
            value={form.workflowGroup}
            disabled
            fullWidth
            size="small"
          />
          <TextField
            label={t(K.API_SVC_WF_NAME)}
            value={form.workflowName}
            disabled
            fullWidth
            size="small"
          />
        </Stack>

        {/* Base URL + Copy */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            label={t(K.API_SVC_BASE_URL)}
            value={baseUrlPreview}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            helperText={!isView ? t(K.API_SVC_BASE_URL_HELP) : undefined}
          />
          <Tooltip title={t(K.API_SVC_COPY_BASE_URL)}>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(baseUrlPreview)}
              sx={{ mt: 0.5, flexShrink: 0 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Custom URL + optional Edit button + Copy */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            flexWrap: 'nowrap',
          }}
        >
          <TextField
            label={t(K.API_SVC_CUSTOM_URL)}
            value={customUrlSummary}
            size="small"
            error={Boolean(errors.customUrl)}
            helperText={errors.customUrl || t(K.API_SVC_CUSTOM_URL_STEP1_HELP)}
            InputProps={{ readOnly: true }}
            sx={{ flex: 1, minWidth: 0 }}
          />
          {!isView && (
            <Button
              variant="contained"
              onClick={() => setCustomDrawerOpen(true)}
              sx={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                height: 35,
                px: 2,
                alignSelf: 'flex-start',
              }}
            >
              {t(K.API_SVC_CUSTOM_URL)}
            </Button>
          )}
          <Tooltip title={t(K.API_SVC_COPY_CUSTOM_URL)}>
            <IconButton
              size="small"
              onClick={() => copyToClipboard(customUrlSummary)}
              sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: 0.5 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Status Switch — tooltip on control only (wrapping FormControlLabel breaks Switch layout) */}
        <FormControlLabel
          sx={{ m: 0, alignItems: 'center' }}
          control={
            <Tooltip
              title={
                isView
                  ? form.enabled
                    ? t(K.API_SVC_CHIP_ENABLED)
                    : t(K.API_SVC_CHIP_DISABLED)
                  : form.enabled
                    ? t(K.API_SVC_MENU_DISABLE)
                    : t(K.API_SVC_MENU_ENABLE)
              }
            >
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Switch
                  size="small"
                  checked={form.enabled}
                  onChange={(e) => onChange('enabled', e.target.checked)}
                  disabled={isView}
                  color="primary"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      transform: 'translateX(12px)',
                    },
                  }}
                />
              </span>
            </Tooltip>
          }
          label={
            <Typography variant="body2" component="span" sx={{ ml: 1 }}>
              {form.enabled ? t(K.API_SVC_CHIP_ENABLED) : t(K.API_SVC_CHIP_DISABLED)}
            </Typography>
          }
        />

        {/* Conformance */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            bgcolor: 'grey.50',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Checkbox
                checked={form.conformance}
                disabled={isView}
                onChange={(_, checked) => {
                  onChange('conformance', checked);
                  if (!checked) {
                    setForm((prev) => ({
                      ...prev,
                      conformance: false,
                      conformanceExecute: false,
                      executeWithin: '',
                      hasNoErrors: false,
                    }));
                  }
                }}
              />
            }
            label={t(K.API_SVC_CONFORMANCE)}
          />

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              mt: 2,
            }}
          >
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={form.conformanceExecute}
                  disabled={conformanceDisabled || isView}
                  onChange={(_, checked) => onChange('conformanceExecute', checked)}
                />
              }
              label={t(K.API_SVC_EXECUTE_WITHIN)}
            />

            <TextField
              placeholder={t(K.API_SVC_EXECUTE_WITHIN_PLACEHOLDER)}
              value={form.executeWithin}
              onChange={(e) => onChange('executeWithin', e.target.value)}
              disabled={conformanceDisabled || !form.conformanceExecute || isView}
              error={Boolean(errors.executeWithin)}
              size="small"
              sx={{ width: 120, flexShrink: 0, m: 0 }}
              inputProps={{ inputMode: 'numeric' }}
            />

            <RadioGroup
              row
              value={form.executeUnit}
              onChange={(e) => onChange('executeUnit', e.target.value)}
              sx={{
                flexShrink: 0,
                gap: 1,
                alignItems: 'center',
                '& .MuiFormControlLabel-root': { m: 0, mr: 1 },
              }}
            >
              <FormControlLabel
                value="ms"
                disabled={conformanceDisabled || !form.conformanceExecute || isView}
                control={<Radio size="small" />}
                label={t(K.API_SVC_EXECUTE_WITHIN_MS)}
              />
              <FormControlLabel
                value="sec"
                disabled={conformanceDisabled || !form.conformanceExecute || isView}
                control={<Radio size="small" />}
                label={t(K.API_SVC_EXECUTE_WITHIN_SEC)}
              />
              <FormControlLabel
                value="min"
                disabled={conformanceDisabled || !form.conformanceExecute || isView}
                control={<Radio size="small" />}
                label={t(K.API_SVC_EXECUTE_WITHIN_MIN)}
              />
            </RadioGroup>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ flexShrink: 0, lineHeight: 1, px: 0.5 }}
            >
              {t(K.API_SVC_AND)}
            </Typography>

            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Checkbox
                  checked={form.hasNoErrors}
                  disabled={conformanceDisabled || isView}
                  onChange={(_, checked) => onChange('hasNoErrors', checked)}
                />
              }
              label={t(K.API_SVC_HAS_NO_ERRORS)}
            />
          </Box>

          {errors.executeWithin && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {errors.executeWithin}
            </Typography>
          )}
        </Box>
      </Stack>
    ) : null;

  return (
    <>
      <Drawer anchor="left" open={!!target} onClose={requestClose}>
        <Box
          sx={{
            width: 920,
            maxWidth: '95vw',
            minWidth: 640,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#f6f7fa',
          }}
        >
          <Box
            sx={{
              bgcolor: '#eceff3',
              borderBottom: '1px solid #d5d9e0',
              px: 1.25,
              py: 0.9,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.1rem',
                color: '#5f6878',
                textTransform: 'uppercase',
                letterSpacing: 0.2,
              }}
            >
              {drawerTitle}
            </Typography>
            <IconButton
              size="small"
              onClick={requestClose}
              aria-label={isView ? t(K.API_SVC_BTN_CLOSE) : t(K.BTN_CANCEL)}
              sx={{ color: '#6a7382', border: '1px solid #d2d8e2', borderRadius: 1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {target && (
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: '1px solid #d5d9e0',
                bgcolor: '#f8f9fc',
                color: '#4b5566',
              }}
            >
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <b>{apiLabel(K.ACCOUNT)}:</b> {account || emDash}
              </Typography>
              <Typography variant="body2">
                <b>{apiLabel(K.LBL_NAME)}:</b> {target.name || detail?.name || emDash}
              </Typography>
              <Typography variant="body2">
                <b>{apiLabel(K.WORKFLOW_GROUP)}:</b> {wfgName || emDash}
              </Typography>
              <Typography variant="body2">
                <b>{apiLabel(K.WORKFLOW)}:</b> {wfName || emDash}
              </Typography>
            </Box>
          )}

          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>{formBody}</Box>

          {form && !isLoading && !loadError && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid #d5d9e0',
                bgcolor: '#fff',
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
              }}
            >
              {isView ? (
                <Button variant="contained" onClick={() => onClose()}>
                  {t(K.API_SVC_BTN_CLOSE)}
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    onClick={handleApplyClick}
                    disabled={applying}
                    startIcon={applying ? <CircularProgress size={14} color="inherit" /> : null}
                  >
                    {applying ? t(K.API_SVC_BTN_APPLYING) : t(K.BTN_APPLY)}
                  </Button>
                  <Button variant="outlined" onClick={handleModifyWorkflow} disabled={applying}>
                    {t(K.API_SVC_BTN_MODIFY_WORKFLOW)}
                  </Button>
                  <Button variant="outlined" onClick={handleCancelClick} disabled={applying}>
                    {t(K.BTN_CANCEL)}
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* ── Custom URL Drawer ─────────────────────────────────────── */}
      {form && (
        <CustomUrlDrawer
          open={customDrawerOpen}
          onClose={() => setCustomDrawerOpen(false)}
          value={form.customUrl}
          baseUrlTo={baseUrlPreview}
          onApply={(cu) => {
            onChange('customUrl', cu);
            setErrors((prev) => ({ ...prev, customUrl: undefined }));
          }}
        />
      )}

      {/* ── Cancel warning dialog ──────────────────────────────────── */}
      <ApiServiceCancelWarningDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirmCancel={handleDiscardChanges}
        onSaveChanges={handleSaveAndClose}
      />

      {/* ── Toast ────────────────────────────────────────────────────── */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default memo(ApiServiceEditDrawer);
